import { useMemo } from "react";
import { useFeatureFlag } from "../useFeatureFlag/useFeatureFlag";
import { useProducts } from "../useProducts/useProducts";
import { Flags } from "../../feature-flags/flags";
import { ADMINCENTER_PRODUCT_ID } from "../../constants/constants";
import { OrganizationProduct } from "../../domain/OrganizationProduct";

interface UseTeamsEligibleProductsParams {
    organizationId?: string;
}

/**
 * Return type for the useEligibleProducts hook
 */
interface UseTeamsEligibleProductsReturn {
    eligibleProducts: OrganizationProduct[];
    isLoading: boolean;
    error?: Error;
}

/**
 * Hook to get products that are eligible for the Teams feature.
 *
 * This hook combines product data with feature flag configuration to determine
 * which products should be available for team creation and management.
 *
 * When USE_SUPPORTS_TEAMS_PROPERTY flag is ON:
 * - Uses the `supportsTeams` product property from the backend
 *
 * When USE_SUPPORTS_TEAMS_PROPERTY flag is OFF (legacy):
 * - Uses the `eligible_product_ids` variable from the ENABLE_TEAM_GROUPS flag
 *
 * In both cases:
 * - The Teams feature flag (Flags.ENABLE_TEAM_GROUPS) must be enabled
 * - The product must have at least one instance with `optiIdEnabled === true`
 * - For Admin Center, the ABAC flag must also be enabled
 *
 */
export const useTeamsEligibleProducts = ({
    organizationId
}: UseTeamsEligibleProductsParams = {}): UseTeamsEligibleProductsReturn => {
    const { products = [], isLoading: productsLoading, error: productsError } = useProducts({ organizationId });
    const abacFlag = useFeatureFlag(Flags.USE_ATTRIBUTE_PERMISSIONS);
    const teamsFlag = useFeatureFlag(Flags.ENABLE_TEAM_GROUPS);
    const { enabled: useSupportsTeamsProperty } = useFeatureFlag(Flags.USE_SUPPORTS_TEAMS_PROPERTY);

    const eligibleProducts = useMemo(() => {
        if (!teamsFlag.enabled) return [];

        if (useSupportsTeamsProperty) {
            return products.filter((product: OrganizationProduct) => {
                if (!product.supportsTeams) return false;
                if (!abacFlag.enabled && product.id === ADMINCENTER_PRODUCT_ID) return false;
                return product.instances.some((instance) => instance.optiIdEnabled);
            });
        }

        if (!teamsFlag.variables || typeof teamsFlag.variables !== "object") {
            return [];
        }

        const eligibleProductIds = Object.keys(
            (teamsFlag.variables.eligible_product_ids as Record<string, boolean>) || {}
        );

        return products.filter((product: OrganizationProduct) => {
            if (!eligibleProductIds.includes(product.id)) return false;
            if (!abacFlag.enabled && product.id === ADMINCENTER_PRODUCT_ID) return false;
            return product.instances.some((instance) => instance.optiIdEnabled);
        });
    }, [products, teamsFlag.enabled, teamsFlag.variables, abacFlag.enabled, useSupportsTeamsProperty]);

    return {
        eligibleProducts,
        isLoading: productsLoading,
        error: productsError
    };
};
